import { useState, useCallback, useRef } from 'react'
import type { RuletaBet } from '../utils/ruletaLogic'
import {
  generateWinningNumber,
  calculateWheelRotation,
  calculateTotalWinnings,
} from '../utils/ruletaLogic'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { recordGameTransaction } from '@/features/stats'

export type SpinPhase = 'idle' | 'betting' | 'spinning' | 'result'

interface UseRuletaSpinReturn {
  phase: SpinPhase
  winningNumber: number | null
  lastNumbers: number[]
  wheelRotation: number
  lastWinnings: number
  isSpinning: boolean
  startBetting: () => void
  spin: (bets: RuletaBet[]) => Promise<void>
  reset: () => void
}

const SPIN_DURATION = 5000 // 5 seconds for spin animation
const MAX_HISTORY = 10 // Keep last 10 numbers

export function useRuletaSpin(): UseRuletaSpinReturn {
  const { user, updateBalance, isDemo } = useAuth()
  const [phase, setPhase] = useState<SpinPhase>('betting')
  const [winningNumber, setWinningNumber] = useState<number | null>(null)
  const [lastNumbers, setLastNumbers] = useState<number[]>([])
  const [wheelRotation, setWheelRotation] = useState(0)
  const [lastWinnings, setLastWinnings] = useState(0)
  const [isSpinning, setIsSpinning] = useState(false)

  const currentBetsRef = useRef<RuletaBet[]>([])

  const startBetting = useCallback(() => {
    setPhase('betting')
    setWinningNumber(null)
    setLastWinnings(0)
  }, [])

  const spin = useCallback(
    async (bets: RuletaBet[]) => {
      if (bets.length === 0 || isSpinning) return

      currentBetsRef.current = bets

      // Deduct total bet from balance
      const totalBet = bets.reduce((sum, bet) => sum + bet.amount, 0)
      await updateBalance(-totalBet)

      setPhase('spinning')
      setIsSpinning(true)

      // Generate winning number
      const number = generateWinningNumber()
      const rotation = calculateWheelRotation(number)

      // Start wheel animation
      setWheelRotation((prev) => prev + rotation)

      // Wait for spin animation
      await new Promise((resolve) => setTimeout(resolve, SPIN_DURATION))

      // Set winning number
      setWinningNumber(number)
      setIsSpinning(false)

      // Add to history
      setLastNumbers((prev) => [number, ...prev.slice(0, MAX_HISTORY - 1)])

      // Calculate winnings
      const winnings = calculateTotalWinnings(bets, number)
      setLastWinnings(winnings)

      // Add winnings to balance
      if (winnings > 0) {
        await updateBalance(winnings)
      }

      // Record game transaction for stats (only for real users)
      if (!isDemo && user) {
        const result = winnings > totalBet ? 'win' : winnings > 0 ? 'push' : 'loss'
        recordGameTransaction(
          user.uid,
          user.email,
          user.displayName || 'Usuario',
          'ruleta',
          totalBet,
          winnings,
          result,
          { winningNumber: number, bets: bets.map(b => ({ type: b.type, amount: b.amount })) }
        )
      }

      setPhase('result')
    },
    [isSpinning, updateBalance, isDemo, user]
  )

  const reset = useCallback(() => {
    setPhase('betting')
    setWinningNumber(null)
    setLastWinnings(0)
  }, [])

  return {
    phase,
    winningNumber,
    lastNumbers,
    wheelRotation,
    lastWinnings,
    isSpinning,
    startBetting,
    spin,
    reset,
  }
}
